import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, ArrowRight } from "lucide-react";
import { POSTS, getPublishedPosts, isPublished } from "../blogData";
import { Navbar } from "../components/Navbar";
import { Contact, Footer } from "../components/Contact";
import { Reveal } from "../components/Reveal";
import { setSeo } from "../lib/seo";

const fmtDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

function Block({ block }) {
  if (block.type === "h2") return <h2 className="font-heading text-2xl sm:text-3xl mt-10 mb-4">{block.text}</h2>;
  if (block.type === "ul") return (
    <ul className="my-5 space-y-2">
      {block.items.map((it) => (
        <li key={it} className="flex items-start gap-3 text-[#3f3f46] font-light">
          <span className="w-1.5 h-1.5 bg-gold rounded-full mt-2.5 shrink-0" /> {it}
        </li>
      ))}
    </ul>
  );
  if (block.type === "links") return (
    <div className="my-6 border-l-2 border-gold pl-5 space-y-2">
      {block.items.map((l) => (
        <Link key={l.to} to={l.to} data-testid={`inline-link-${l.to.split("/").pop()}`} className="block text-gold hover:underline font-medium">
          {l.text} →
        </Link>
      ))}
    </div>
  );
  return <p className="text-[#3f3f46] font-light text-lg leading-relaxed my-5">{block.text}</p>;
}

export default function BlogPost() {
  const { slug } = useParams();
  const found = POSTS.find((p) => p.slug === slug);
  const post = isPublished(found) ? found : null;
  const others = getPublishedPosts().filter((p) => p.slug !== slug).slice(0, 3);

  useEffect(() => {
    if (post) setSeo({ title: `${post.title} | Melissa O'Donnell Real Estate`, description: post.metaDescription });
    window.scrollTo(0, 0);
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">
        <h1 className="font-heading text-4xl font-light">Post not found</h1>
        <Link to="/blog" className="mt-6 inline-flex items-center gap-2 text-gold hover:underline" data-testid="blogpost-back">
          <ArrowLeft size={16} /> Back to blog
        </Link>
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <main data-testid={`blog-post-${post.slug}`}>
        <article>
          <header className="pt-36 pb-14 bg-[#0a0a0a] text-white">
            <div className="max-w-3xl mx-auto px-6 md:px-12">
              <Link to="/blog" className="inline-flex items-center gap-2 text-white/60 hover:text-gold text-sm transition-colors mb-8" data-testid="blogpost-back">
                <ArrowLeft size={16} /> All posts
              </Link>
              <p className="eyebrow text-gold mb-4">{post.category}</p>
              <h1 className="font-heading font-light text-4xl sm:text-5xl tracking-tight leading-tight">{post.title}</h1>
              <div className="flex items-center gap-5 mt-6 text-white/50 text-sm">
                <span className="flex items-center gap-1.5"><Calendar size={14} /> {fmtDate(post.date)}</span>
                <span className="flex items-center gap-1.5"><Clock size={14} /> {post.readTime}</span>
              </div>
            </div>
          </header>

          <div className="py-16 md:py-20 bg-white">
            <div className="max-w-3xl mx-auto px-6 md:px-12">
              {post.content.map((b, i) => <Block key={i} block={b} />)}

              <div className="mt-12 bg-surface border border-black/5 p-8 text-center">
                <h3 className="font-heading text-2xl">Ready to make your move?</h3>
                <p className="text-[#52525b] font-light mt-3">Get expert, local guidance from Melissa O'Donnell.</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
                  <Link to="/#home-value" data-testid="blogpost-cta-value" className="bg-gold text-white text-sm px-7 py-3.5 hover:bg-gold-hover transition-colors">What's my home worth?</Link>
                  <Link to="/#contact" data-testid="blogpost-cta-contact" className="border border-black/20 text-sm px-7 py-3.5 hover:bg-[#0a0a0a] hover:text-white transition-colors">Get in touch</Link>
                </div>
              </div>
            </div>
          </div>
        </article>

        <section className="pb-20 md:pb-28 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h3 className="font-heading text-2xl mb-8">Keep reading</h3>
            <div className="grid sm:grid-cols-3 gap-6">
              {others.map((p) => (
                <Reveal key={p.slug}>
                  <Link to={`/blog/${p.slug}`} data-testid={`related-${p.slug}`} className="group block bg-surface border border-black/5 p-6 h-full hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                    <span className="eyebrow text-[10px] text-gold">{p.category}</span>
                    <p className="font-heading text-lg mt-2 leading-snug group-hover:text-gold transition-colors">{p.title}</p>
                    <span className="inline-flex items-center gap-1.5 text-sm mt-4 text-[#0a0a0a] group-hover:text-gold transition-colors">Read <ArrowRight size={14} /></span>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
        <Contact />
      </main>
      <Footer />
    </>
  );
}
